import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, fmtMoney } from "@/lib/api";
import Modal, { inputCls, inputErrCls, FieldError, labelCls, btnPrimary, btnAccent, btnOutline } from "@/components/Modal";
import NumberInput from "@/components/NumberInput";
import { useFormErrors } from "@/lib/formErrors";
import { useAuth } from "@/context/AuthContext";
import { TableCard, EmptyRow, theadCls, thCls, thNumCls, tdCls, tdNumCls, rowCls } from "@/components/Table";
import { Plus, Truck, Edit2, Trash2, Landmark, HandCoins, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", contact: "", phone: "", address: "", categories: [] };

function Stat({ label, value, icon: Icon, tone = "forest", hint }) {
  const toneCls = tone === "danger" ? "text-agri-danger bg-red-50" : tone === "amber" ? "text-agri-amber-dark bg-agri-amber/10" : "text-agri-forest bg-agri-forest/10";
  return (
    <div className="card-soft p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="label-cap">{label}</div>
          <div className="mt-2 text-2xl font-bold tabular-nums text-agri-ink truncate">{value}</div>
          {hint && <div className="text-[11px] text-agri-ink-2 mt-1">{hint}</div>}
        </div>
        <div className={`h-10 w-10 shrink-0 rounded-lg grid place-items-center ${toneCls}`}><Icon size={18} /></div>
      </div>
    </div>
  );
}

export default function Suppliers() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [payOpen, setPayOpen] = useState(null);
  const [payAmt, setPayAmt] = useState(0);

  const { data = [] } = useQuery({ queryKey: ["suppliers"], queryFn: () => api.get("/suppliers").then((r) => r.data) });

  const fe = useFormErrors();
  const openNew = () => { setEditing(null); setForm(empty); fe.clear(); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...empty, ...s }); fe.clear(); setOpen(true); };
  const save = useMutation({
    mutationFn: () => editing ? api.put(`/suppliers/${editing.id}`, form) : api.post("/suppliers", form),
    onSuccess: () => { toast.success(editing ? "Supplier updated" : "Supplier added"); qc.invalidateQueries({ queryKey: ["suppliers"] }); setOpen(false); setForm(empty); setEditing(null); fe.clear(); },
    onError: (e) => fe.handleError(e, toast.error),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/suppliers/${id}`),
    onSuccess: () => { toast.success("Supplier deleted"); qc.invalidateQueries({ queryKey: ["suppliers"] }); qc.invalidateQueries({ queryKey: ["payables"] }); },
    onError: (e) => fe.handleError(e, toast.error),
  });
  const pay = useMutation({
    mutationFn: () => api.post("/supplier-payments", { supplier_id: payOpen.id, amount: parseFloat(payAmt), method: "cash" }),
    onSuccess: () => { toast.success("Payment recorded"); qc.invalidateQueries({ queryKey: ["suppliers"] }); qc.invalidateQueries({ queryKey: ["payables"] }); qc.invalidateQueries({ queryKey: ["notifications"] }); setPayOpen(null); setPayAmt(0); fe.clear(); },
    onError: (e) => fe.handleError(e, toast.error),
  });

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div><div className="label-cap">Network</div><h1 className="font-heading text-3xl mt-1">Suppliers</h1></div>
        <button data-testid="add-supplier-btn" onClick={openNew} className={btnAccent + " flex items-center gap-2"}><Plus size={16} /> Add Supplier</button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Suppliers" value={data.length} icon={Truck} hint="in your network" />
        <Stat label="You Owe" value={fmtMoney(data.reduce((s, x) => s + Math.max(x.pending_payment || 0, 0), 0))} icon={Landmark} tone={data.some((x) => (x.pending_payment || 0) > 0) ? "danger" : "forest"} hint="outstanding payable" />
        <Stat label="Advances Held" value={fmtMoney(data.reduce((s, x) => s + Math.max(-(x.pending_payment || 0), 0), 0))} icon={HandCoins} hint="your money with suppliers" />
        <Stat label="Total Purchased" value={fmtMoney(data.reduce((s, x) => s + (x.total_purchases || 0), 0))} icon={ShoppingBag} tone="amber" hint="lifetime" />
      </div>

      <div className="text-xs text-agri-ink-2">{data.length} supplier{data.length === 1 ? "" : "s"}</div>
      <TableCard>
        <thead className={theadCls}>
          <tr>
            <th className={thCls}>Supplier</th>
            <th className={thCls}>Contact</th>
            <th className={thCls}>Categories</th>
            <th className={thNumCls}>Total Purchases</th>
            <th className={thNumCls}>Pending</th>
            <th className={thCls}></th>
          </tr>
        </thead>
        <tbody>
          {data.length === 0 && <EmptyRow colSpan={6} icon={Truck}>No suppliers yet.</EmptyRow>}
          {data.map((s) => (
            <tr key={s.id} className={rowCls}>
              <td className={`${tdCls} font-medium rt-primary`}><Link to={`/suppliers/${s.id}`} className="text-agri-forest hover:underline">{s.name}</Link></td>
              <td data-label="Contact" className={`${tdCls} text-agri-ink-2`}>{s.contact}<br /><span className="text-xs">{s.phone}</span></td>
              <td data-label="Categories" className={`${tdCls} text-xs`}>{s.categories?.join(", ")}</td>
              <td data-label="Total Purchases" className={tdNumCls}>{fmtMoney(s.total_purchases)}</td>
              <td data-label="Pending" className={`${tdNumCls} font-semibold ${s.pending_payment > 0.001 ? "text-agri-danger" : s.pending_payment < -0.001 ? "text-agri-forest" : "text-agri-ink-2"}`}>
                {s.pending_payment < -0.001 ? `${fmtMoney(-s.pending_payment)} adv` : fmtMoney(s.pending_payment)}
              </td>
              <td className={`${tdCls} text-right whitespace-nowrap rt-actions`}>
                {s.pending_payment > 0 && <button onClick={() => { fe.clear(); setPayOpen(s); setPayAmt(s.pending_payment); }} className="text-xs font-medium text-agri-amber-dark hover:underline mr-2 align-middle">Pay</button>}
                {isAdmin && (
                  <>
                    <button title="Edit" onClick={() => openEdit(s)} className="p-1.5 rounded hover:bg-black/5 text-agri-ink-2 hover:text-agri-forest align-middle"><Edit2 size={15} /></button>
                    <button title="Delete" onClick={() => window.confirm(`Delete supplier "${s.name}"?`) && del.mutate(s.id)} className="p-1.5 rounded hover:bg-red-50 text-agri-ink-2 hover:text-agri-danger ml-1 align-middle"><Trash2 size={15} /></button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </TableCard>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Supplier" : "Add Supplier"}>
        <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="space-y-3">
          <div>
            <label className={labelCls}>Name</label>
            <input required value={form.name}
              onChange={(e) => { setForm({ ...form, name: e.target.value }); fe.clearField("name"); }}
              className={inputCls + (fe.fieldError("name") ? " " + inputErrCls : "")} />
            <FieldError>{fe.fieldError("name")}</FieldError>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Contact person</label><input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>Phone</label><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} /></div>
          </div>
          <div><label className={labelCls}>Address</label><input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>Categories (comma separated)</label>
            <input value={form.categories.join(", ")} onChange={(e) => setForm({ ...form, categories: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className={inputCls} placeholder="Insecticide, Fertilizer" /></div>
          <div className="flex justify-end gap-2 pt-2"><button type="button" onClick={() => setOpen(false)} className={btnOutline}>Cancel</button><button className={btnPrimary} disabled={save.isPending}>{editing ? "Update Supplier" : "Add Supplier"}</button></div>
        </form>
      </Modal>

      <Modal open={!!payOpen} onClose={() => setPayOpen(null)} title={`Pay ${payOpen?.name || ""}`}>
        <form onSubmit={(e) => { e.preventDefault(); pay.mutate(); }} className="space-y-3">
          <div>
            <label className={labelCls}>Amount</label>
            <NumberInput required value={payAmt}
              onChange={(n) => { setPayAmt(n); fe.clearField("amount"); }}
              className={fe.fieldError("amount") ? inputErrCls : ""} />
            <FieldError>{fe.fieldError("amount")}</FieldError>
          </div>
          <div className="flex justify-end gap-2 pt-2"><button type="button" onClick={() => setPayOpen(null)} className={btnOutline}>Cancel</button><button className={btnPrimary} disabled={pay.isPending}>Record payment</button></div>
        </form>
      </Modal>
    </div>
  );
}
